import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type NotificationChannel = 'slack' | 'teams';

export interface NotificationChannelState {
  channel:    NotificationChannel;
  enabled:    boolean;
  configured: boolean;
  maskedUrl?: string;
  updatedAt?: string;
}

export interface TestChannelResult {
  channel: NotificationChannel;
  success: boolean;
  message: string;
}

export interface EmailChannelState {
  channel:      'email';
  enabled:      boolean;
  configured:   boolean;
  recipientIds: string[];
  updatedAt?:   string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/notifications`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Returns the current user's webhook config for the given channel.
   * Returns null on a 404 (never configured) or unexpected error.
   * The full webhook URL is never included in the response.
   */
  getChannel(channel: NotificationChannel): Observable<NotificationChannelState | null> {
    return this.http.get<NotificationChannelState>(`${this.base}/${channel}`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /**
   * Saves the webhook config for the given channel.
   * If webhookUrl is omitted, the existing URL is preserved.
   * The response never includes the full webhook URL.
   */
  saveChannel(
    channel: NotificationChannel,
    data: { enabled: boolean; webhookUrl?: string },
  ): Observable<NotificationChannelState> {
    return this.http.put<NotificationChannelState>(`${this.base}/${channel}`, data);
  }

  /**
   * Sends a test notification using the server-side stored webhook URL.
   * The URL is never sent from the client — the server reads it from storage.
   */
  testChannel(channel: NotificationChannel): Observable<TestChannelResult> {
    return this.http.post<TestChannelResult>(`${this.base}/${channel}/test`, {});
  }

  /**
   * Removes the webhook config for the given channel.
   */
  removeChannel(channel: NotificationChannel): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/${channel}`);
  }

  /** Returns the current email notification config (recipient list). */
  getEmailConfig(): Observable<EmailChannelState> {
    return this.http.get<EmailChannelState>(`${this.base}/email`).pipe(
      catchError(() => of({ channel: 'email' as const, enabled: false, configured: false, recipientIds: [] })),
    );
  }

  /** Saves the email notification config. recipientIds must be active org member userIds. */
  saveEmailConfig(enabled: boolean, recipientIds: string[]): Observable<EmailChannelState> {
    return this.http.put<EmailChannelState>(`${this.base}/email`, { enabled, recipientIds });
  }

  /** Removes the email notification config. */
  removeEmailConfig(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/email`);
  }
}
